<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { timeSeriesApi } from '../lib/api';
  import type { TimeSeriesMeta } from '../lib/types';
  import { toString } from '../lib/id-utils';
  
  export let show: boolean = false;
  export let excludeId: string | number = '';
  export let multiSelect: boolean = true;
  export let selectedIds: string[] = [];
  
  const dispatch = createEventDispatcher();
  
  let series: TimeSeriesMeta[] = [];
  let loading: boolean = false;
  let error: string = '';
  let searchTerm: string = '';
  let page: number = 0;
  let totalSeries: number = 0;
  const pageSize: number = 10;
  
  // Load time series on mount
  onMount(() => {
    if (show) {
      loadSeries();
    }
  });
  
  // Watch for show prop changes
  $: if (show) {
    loadSeries();
  }
  
  // Filter series based on search term
  $: filteredSeries = series.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.description && s.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Load series data
  async function loadSeries() {
    try {
      loading = true;
      error = '';
      
      const response = await timeSeriesApi.getAll(page * pageSize, pageSize);
      
      // Convert excludeId to string for comparison
      const excludeIdString = toString(excludeId);
      series = response.data.filter(s => toString(s.id) !== excludeIdString);
      totalSeries = response.pagination.total;
      
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load time series';
    } finally {
      loading = false;
    }
  }
  
  // Handle page change
  function changePage(newPage: number) {
    if (newPage >= 0 && newPage * pageSize < totalSeries) {
      page = newPage;
      loadSeries();
    }
  }
  
  // Handle series selection
  function toggleSelection(id: string | number) {
    const idStr = toString(id);
    
    if (multiSelect) {
      const index = selectedIds.indexOf(idStr);
      if (index > -1) {
        selectedIds.splice(index, 1);
        selectedIds = [...selectedIds];
      } else {
        selectedIds = [...selectedIds, idStr];
      }
    } else {
      selectedIds = [idStr];
      close(true);
    }
  }
  
  // Check if a series is selected
  function isSelected(id: string | number): boolean {
    const idStr = toString(id);
    return selectedIds.includes(idStr);
  }
  
  // Close modal
  function close(confirmed: boolean = false) {
    if (confirmed) {
      dispatch('select', { ids: selectedIds });
    }
    dispatch('close');
  }
</script>

{#if show}
<div class="modal-backdrop" role="presentation" on:click={() => close()} on:keydown={(e) => e.key === 'Escape' && close()}>
  <div class="modal-dialog" role="dialog" aria-modal="true" on:click|stopPropagation>
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Выбор временных рядов</h5>
        <button type="button" class="btn-close" on:click={() => close()}></button>
      </div>
      
      <div class="modal-body">
        {#if error}
          <div class="alert alert-danger" role="alert">
            {error}
          </div>
        {/if}
        
        <!-- Search -->
        <div class="mb-3">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Поиск по имени или описанию" 
            bind:value={searchTerm}
          />
        </div>
        
        <!-- Series list -->
        {#if loading}
          <div class="text-center py-3">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        {:else if filteredSeries.length === 0}
          <div class="text-center py-3">
            <p class="text-muted">Нет доступных временных рядов</p>
          </div>
        {:else}
          <div class="list-group mb-3">
            {#each filteredSeries as series}
              <button 
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center {isSelected(series.id) ? 'active' : ''}" 
                on:click={() => toggleSelection(series.id)}
              >
                <div>
                  <div class="fw-bold">{series.name}</div>
                  {#if series.description}
                    <small class="text-muted">{series.description}</small>
                  {/if}
                </div>
                {#if isSelected(series.id)}
                  <i class="bi bi-check-circle"></i>
                {/if}
              </button>
            {/each}
          </div>
          
          <!-- Pagination -->
          {#if totalSeries > pageSize}
            <div class="d-flex justify-content-between align-items-center">
              <button 
                class="btn btn-sm btn-outline-primary" 
                disabled={page === 0}
                on:click={() => changePage(page - 1)}
              >
                <i class="bi bi-chevron-left"></i> Предыдущая
              </button>
              
              <span>Страница {page + 1} из {Math.ceil(totalSeries / pageSize)}</span>
              
              <button 
                class="btn btn-sm btn-outline-primary" 
                disabled={(page + 1) * pageSize >= totalSeries}
                on:click={() => changePage(page + 1)}
              >
                Следующая <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          {/if}
        {/if}
      </div>
      
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" on:click={() => close()}>Отмена</button>
        <button 
          type="button" 
          class="btn btn-primary" 
          on:click={() => close(true)}
          disabled={selectedIds.length === 0}
        >
          Выбрать ({selectedIds.length})
        </button>
      </div>
    </div>
  </div>
</div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
  }
  
  .modal-dialog {
    width: 100%;
    max-width: 600px;
    margin: 1.75rem auto;
  }
  
  .modal-content {
    position: relative;
    background-color: #fff;
    border-radius: 0.3rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
  }
  
  .modal-body {
    position: relative;
    padding: 1rem;
    max-height: 60vh;
    overflow-y: auto;
  }
  
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 1rem;
    border-top: 1px solid #dee2e6;
  }
</style>